/**
 * Cut: drag a line across a shape and it really comes apart.
 *
 * Each piece is labelled with how much of the whole shape it is, worked out
 * from its actual area — so "a half" is a thing the child produced and the app
 * measured, not a thing the app arranged in advance. Cut a half in half and it
 * says a quarter, because it is one.
 */

import { useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { Home, Images, Trash2 } from 'lucide-preact';
import type { Vec } from '../../engine/vec.ts';
import type { Body } from '../../engine/body.ts';
import { createBody } from '../../engine/body.ts';
import type { Stroke } from '../../engine/stroke.ts';
import { convexHull, polygonArea, regularPolygon } from '../../engine/geom.ts';
import { splitPolygon } from '../../engine/clip.ts';
import { World } from '../../engine/world.ts';
import type { Spark } from '../../engine/render.ts';
import { drawBody, drawBodyShadow, drawSparks, spawnSparks, updateSparks } from '../../engine/render.ts';
import { partyColors, token } from '../../engine/palette.ts';
import { degreeForSize, play, playImpact, playRatio } from '../../engine/audio.ts';
import { useSurface } from '../../hooks/useSurface.ts';
import { shareLabel } from '../../lib/fractions.ts';
import type { Scene } from '../../lib/scene.ts';
import { sceneBounds } from '../../lib/scene.ts';
import { PicturePicker } from '../../ui/PicturePicker.tsx';
import { Coach, ConfirmSheet, CrayonRow, Stepper, ToyButton } from '../../ui/controls.tsx';

interface Props {
  onExit: () => void;
  tabs: ComponentChildren;
}

/** The world outline of a body — its collider, moved and turned into place. */
function worldVerts(body: Body): Vec[] {
  const cos = Math.cos(body.angle);
  const sin = Math.sin(body.angle);
  return body.verts.map((v) => ({
    x: body.pos.x + v.x * cos - v.y * sin,
    y: body.pos.y + v.x * sin + v.y * cos,
  }));
}

function polygonStroke(points: Vec[], color: string): Stroke {
  const closed = [...points, points[0]];
  return {
    brush: 'marker',
    color: 'rgba(44, 37, 69, 0.85)',
    width: 5,
    points: closed,
    speeds: closed.map(() => 0),
    seed: Math.floor(Math.random() * 1000),
    fill: color,
  };
}

export function FractionToy({ onExit, tabs }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const worldRef = useRef(new World({ width: 1, height: 1, gravity: 1500 }));
  const sparksRef = useRef<Spark[]>([]);
  /** How much of the original shape each body is. */
  const sharesRef = useRef(new Map<number, number>());
  const cutRef = useRef<{ from: Vec; to: Vec } | null>(null);
  const colorRef = useRef(token('--crayon-orange'));

  const [sides, setSides] = useState(4);
  const [color, setColor] = useState(() => token('--crayon-orange'));
  const [pieces, setPieces] = useState(0);
  const [askClear, setAskClear] = useState(false);
  const [picking, setPicking] = useState(false);
  colorRef.current = color;

  const dropWhole = (width: number, height: number, sideCount: number) => {
    const world = worldRef.current;
    world.clear();
    sharesRef.current.clear();
    const radius = Math.min(width, height) * 0.22;
    const points = regularPolygon(sideCount, radius).map((p) => ({
      x: p.x + width / 2,
      y: p.y + height * 0.38,
    }));
    // The whole shape hangs still, waiting to be cut. Letting it fall first
    // means a child aiming at the middle of the paper slices empty air, and
    // the shape they were shown is not the shape that is there any more.
    const body = world.add(createBody(points, [polygonStroke(points, colorRef.current)], { static: true }));
    sharesRef.current.set(body.id, 1);
    setPieces(1);
  };

  /**
   * One of the child's own drawings, hung up to be cut. Its outline is the
   * hull of the marks, which is what makes cutting it meaningful — you can
   * halve your own rocket and it tells you which piece is the bigger half.
   */
  const hangPicture = (scene: Scene) => {
    const world = worldRef.current;
    const box = sceneBounds(scene);
    if (!box) return;

    const boxW = Math.max(1, box.maxX - box.minX);
    const boxH = Math.max(1, box.maxY - box.minY);
    const scale = Math.min((world.width * 0.6) / boxW, (world.height * 0.42) / boxH, 2);
    const cx = (box.minX + box.maxX) / 2;
    const cy = (box.minY + box.maxY) / 2;
    const toX = world.width / 2;
    const toY = world.height * 0.38;

    const points: Vec[] = [];
    for (const s of scene.strokes) {
      for (const p of s.points) points.push({ x: toX + (p.x - cx) * scale, y: toY + (p.y - cy) * scale });
    }
    // The outline, not the scribble: drawing a stroke through every captured
    // point in order would trace a jagged mess rather than the shape's edge.
    const hull = convexHull(points);
    if (hull.length < 3) return;

    world.clear();
    sharesRef.current.clear();
    const body = world.add(createBody(hull, [polygonStroke(hull, colorRef.current)], { static: true }));
    sharesRef.current.set(body.id, 1);
    setPieces(1);
    setPicking(false);
  };

  /** Cut every shape the line crosses, and share out each one's fraction. */
  const cutAlong = (from: Vec, to: Vec) => {
    const world = worldRef.current;
    let cuts = 0;
    for (const body of [...world.bodies]) {
      const outline = worldVerts(body);
      const halves = splitPolygon(outline, from, to);
      if (!halves) continue;

      const whole = Math.abs(polygonArea(outline));
      if (whole <= 1) continue;
      const share = sharesRef.current.get(body.id) ?? 1;

      world.remove(body.id);
      sharesRef.current.delete(body.id);

      for (const half of halves) {
        const area = Math.abs(polygonArea(half));
        if (area < whole * 0.02) continue;
        const piece = world.add(
          // A gentle shove apart, not a launch — the pieces have to stay where
          // the child can read the fraction written on them.
          createBody(half, [polygonStroke(half, colorRef.current)], {
            vel: { x: (Math.random() - 0.5) * 60, y: -40 },
            angVel: (Math.random() - 0.5) * 0.8,
          })
        );
        sharesRef.current.set(piece.id, share * (area / whole));
      }
      cuts++;
    }

    if (cuts > 0) {
      // Each piece sings its own share. Halving a string doubles its pitch, so
      // a half rings an octave above the whole, two thirds a fifth, three
      // quarters a fourth — the ratios harmony is actually made of. A child
      // cutting a shape in half hears the octave without being told.
      [...world.bodies]
        .map((b) => sharesRef.current.get(b.id))
        .filter((s): s is number => s !== undefined && s < 0.999)
        .sort((a, b) => b - a)
        .slice(0, 3)
        .forEach((share, i) => playRatio(1 / share, { delay: i * 0.14, duration: 0.5 }));
      play('whoosh', 0.7);
      spawnSparks(sparksRef.current, { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 }, partyColors(), 18, 380);
      setPieces(world.bodies.length);
    }
  };

  useSurface(canvasRef, {
    onResize: ({ width, height }) => {
      worldRef.current.resize(width, height);
      if (worldRef.current.bodies.length === 0) dropWhole(width, height, sides);
    },

    onFrame: (ctx, dt, { width, height }) => {
      const world = worldRef.current;
      world.update(dt);
      updateSparks(sparksRef.current, dt);
      for (const impact of world.impacts) playImpact('pop', Math.min(1, impact.speed / 1600), degreeForSize(impact.radius));

      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      for (const body of world.bodies) drawBodyShadow(ctx, body, height);
      for (const body of world.bodies) drawBody(ctx, body);

      // Every piece wears its own share of the original shape.
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (const body of world.bodies) {
        const share = sharesRef.current.get(body.id);
        if (share === undefined || share > 0.999) continue;
        const label = shareLabel(share);
        const size = Math.max(11, Math.min(21, body.radius * 0.42));
        ctx.font = `800 ${size}px ui-rounded, system-ui, sans-serif`;
        ctx.lineWidth = 4;
        ctx.strokeStyle = token('--paper');
        ctx.strokeText(label, body.pos.x, body.pos.y);
        ctx.fillStyle = token('--ink');
        ctx.fillText(label, body.pos.x, body.pos.y);
      }
      ctx.restore();

      // The cut being made, drawn as a dashed line right across the paper.
      const cut = cutRef.current;
      if (cut) {
        ctx.save();
        ctx.strokeStyle = token('--crayon-red');
        ctx.lineWidth = 3;
        ctx.setLineDash([10, 8]);
        const dx = cut.to.x - cut.from.x;
        const dy = cut.to.y - cut.from.y;
        const length = Math.hypot(dx, dy) || 1;
        const far = Math.hypot(width, height);
        ctx.beginPath();
        ctx.moveTo(cut.from.x - (dx / length) * far, cut.from.y - (dy / length) * far);
        ctx.lineTo(cut.from.x + (dx / length) * far, cut.from.y + (dy / length) * far);
        ctx.stroke();
        ctx.restore();
      }

      drawSparks(ctx, sparksRef.current);
    },

    onDown: (p) => {
      cutRef.current = { from: { ...p.pos }, to: { ...p.pos } };
    },
    onMove: (p) => {
      if (cutRef.current) cutRef.current.to = { ...p.pos };
    },
    onUp: (p) => {
      const cut = cutRef.current;
      cutRef.current = null;
      if (!cut) return;
      const travelled = Math.hypot(p.pos.x - cut.from.x, p.pos.y - cut.from.y);
      // A tap is not a cut — it takes a real swipe to slice something.
      if (travelled < 30) return;
      cutAlong(cut.from, { ...p.pos });
    },
  });

  const restart = (nextSides = sides) => {
    const canvas = canvasRef.current;
    const rect = canvas?.getBoundingClientRect();
    setSides(nextSides);
    setAskClear(false);
    if (rect) dropWhole(rect.width, rect.height, nextSides);
    play('whoosh', 0.6);
  };

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Cut a shape into fractions" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <ToyButton round label="A whole shape again" hideLabel onClick={() => setAskClear(true)}>
            <Trash2 size={24} strokeWidth={2.5} />
          </ToyButton>
        </div>

        <p class="readout">
          <strong>
            {pieces} piece{pieces === 1 ? '' : 's'}
          </strong>
          <span>{pieces === 1 ? 'still a whole one' : 'they still add up to one whole'}</span>
        </p>

        <Coach>Drag a line right across the shape to cut it</Coach>
      </div>

      <div class="dock">
        {tabs}
        <div class="dock__row">
          <Stepper value={sides} min={3} max={12} label="corners" onChange={(n) => restart(n)} />
          <CrayonRow value={color} onChange={setColor} />
        </div>
        <div class="dock__row">
          <ToyButton small label="Cut one of my pictures" onClick={() => setPicking(true)}>
            <Images size={20} strokeWidth={2.75} />
          </ToyButton>
        </div>
      </div>

      {picking && (
        <PicturePicker title="Which picture shall we cut?" onPick={hangPicture} onCancel={() => setPicking(false)} />
      )}

      {askClear && (
        <ConfirmSheet
          title="Put the shape back together?"
          confirmLabel="Start again"
          onConfirm={() => restart()}
          onCancel={() => setAskClear(false)}
        />
      )}
    </div>
  );
}
